import * as React from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, ShieldCheck, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/field';
import { env } from '@/lib/env';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_app/operator/audit')({
  staticData: { crumb: 'Audit log' },
  beforeLoad: ({ context }) => {
    const ctx = context as { session: { role: string } };
    if (ctx.session.role !== 'operator') throw redirect({ to: '/' });
  },
  component: OperatorAudit,
});

interface AuditRow {
  id: number;
  ts: number;
  actor: string;
  action: string;
  target: string;
  payload_json: string;
  prev_hash: string;
  hash: string;
}

interface ListResponse {
  rows: AuditRow[];
  next_cursor: number | null;
}

interface VerifyResponse {
  total_rows: number;
  tampered_rows: number;
  first_tampered_id: number | null;
  tampered_ids: number[];
  missing_hashes: number;
}

function OperatorAudit() {
  const [actor, setActor] = React.useState('');
  const [action, setAction] = React.useState('');
  const [target, setTarget] = React.useState('');
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');
  const [appliedFilters, setAppliedFilters] = React.useState({
    actor: '',
    action: '',
    target: '',
    from: '',
    to: '',
  });

  const list = useQuery({
    queryKey: ['operator', 'audit', appliedFilters],
    queryFn: async (): Promise<ListResponse> => {
      const url = new URL(`${env.apiBaseUrl}/api/operator/audit`, window.location.origin);
      if (appliedFilters.actor) url.searchParams.set('actor', appliedFilters.actor);
      if (appliedFilters.action) url.searchParams.set('action', appliedFilters.action);
      if (appliedFilters.target) url.searchParams.set('target', appliedFilters.target);
      if (appliedFilters.from)
        url.searchParams.set('from', String(Math.floor(new Date(appliedFilters.from).getTime() / 1000)));
      if (appliedFilters.to)
        url.searchParams.set('to', String(Math.floor(new Date(appliedFilters.to).getTime() / 1000)));
      url.searchParams.set('limit', '200');
      const res = await fetch(url.toString(), { credentials: 'include' });
      if (!res.ok) throw new Error('failed_to_load');
      return (await res.json()) as ListResponse;
    },
  });

  const qc = useQueryClient();
  const verify = useMutation({
    mutationFn: async (): Promise<VerifyResponse> => {
      const res = await fetch(`${env.apiBaseUrl}/api/operator/audit/verify`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('verify_failed');
      return (await res.json()) as VerifyResponse;
    },
  });
  const backfill = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${env.apiBaseUrl}/api/operator/audit/backfill`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('backfill_failed');
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operator', 'audit'] });
      verify.reset();
    },
  });

  const apply = () => setAppliedFilters({ actor, action, target, from, to });

  const tamperedSet = new Set(verify.data?.tampered_ids ?? []);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
            Audit log
          </h1>
          <p className="mt-1 text-sm text-text-muted">
            Pełna historia operacji. Łańcuch hash-chain pozwala wykryć ingerencję
            w bazę poza-aplikacyjną.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => verify.mutate()}
            disabled={verify.isPending}
          >
            <ShieldCheck className="size-4" aria-hidden="true" />
            {verify.isPending ? 'Weryfikuję…' : 'Sprawdź integralność'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              if (
                confirm(
                  'Uzupełnić brakujące hashe? To zapisze prev_hash/hash dla wszystkich legacy wierszy bez nadpisywania już-zapisanych.'
                )
              ) {
                backfill.mutate();
              }
            }}
            disabled={backfill.isPending}
          >
            Uzupełnij hashe legacy
          </Button>
        </div>
      </header>

      {verify.data ? <VerifyResult v={verify.data} /> : null}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="size-4" aria-hidden="true" /> Filtry
          </CardTitle>
          <CardDescription>
            Pole tekstowe pasuje przez SQL LIKE — wpisz dowolny fragment.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              apply();
            }}
            className="grid gap-3 md:grid-cols-5"
          >
            <Field label="Actor" htmlFor="actor">
              <Input id="actor" value={actor} onChange={(e) => setActor(e.target.value)} />
            </Field>
            <Field label="Action" htmlFor="action">
              <Input id="action" value={action} onChange={(e) => setAction(e.target.value)} />
            </Field>
            <Field label="Target" htmlFor="target">
              <Input id="target" value={target} onChange={(e) => setTarget(e.target.value)} />
            </Field>
            <Field label="Od (data)" htmlFor="from">
              <Input id="from" type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label="Do (data)" htmlFor="to">
              <Input id="to" type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
            <div className="md:col-span-5 flex justify-end">
              <Button type="submit">Zastosuj</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            {list.data ? `${list.data.rows.length} wierszy` : 'Ładowanie…'}
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          {list.data && list.data.rows.length > 0 ? (
            <table className="w-full min-w-[800px] text-xs">
              <thead className="bg-surface text-left text-[10px] uppercase tracking-wide text-text-subtle">
                <tr>
                  <th className="px-3 py-2 font-semibold">ID</th>
                  <th className="px-3 py-2 font-semibold">Kiedy</th>
                  <th className="px-3 py-2 font-semibold">Actor</th>
                  <th className="px-3 py-2 font-semibold">Action</th>
                  <th className="px-3 py-2 font-semibold">Target</th>
                  <th className="px-3 py-2 font-semibold w-12">·</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {list.data.rows.map((r) => (
                  <AuditRowRender
                    key={r.id}
                    row={r}
                    tampered={tamperedSet.has(r.id)}
                  />
                ))}
              </tbody>
            </table>
          ) : list.isPending ? null : (
            <p className="p-4 text-sm text-text-muted">Brak wpisów dla tych filtrów.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function VerifyResult({ v }: { v: VerifyResponse }) {
  if (v.tampered_rows > 0) {
    return (
      <p className="flex items-start gap-2 rounded-md bg-error/10 p-3 text-sm text-error ring-1 ring-error/20">
        <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
        Wykryto {v.tampered_rows} zafałszowanych wierszy (najwcześniejszy ID:{' '}
        <span className="font-mono">{v.first_tampered_id}</span>). Sprawdź czy ktoś
        modyfikował \`audit_log\` poza aplikacją.
      </p>
    );
  }
  return (
    <p className="rounded-md bg-success/10 p-3 text-sm text-success ring-1 ring-success/20">
      Integralność OK — {v.total_rows} wierszy, {v.missing_hashes} bez zapisanego
      hash-a (legacy). Kliknij „Uzupełnij hashe legacy" aby je policzyć i zapisać.
    </p>
  );
}

function AuditRowRender({ row, tampered }: { row: AuditRow; tampered: boolean }) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <tr className={cn(tampered && 'bg-error/5')}>
        <td className="px-3 py-2 font-mono text-text-subtle">{row.id}</td>
        <td className="px-3 py-2 whitespace-nowrap text-text">
          {new Date(row.ts * 1000).toLocaleString('pl-PL')}
        </td>
        <td className="px-3 py-2 font-mono text-xs text-text">{row.actor}</td>
        <td className="px-3 py-2 font-mono text-xs text-text">{row.action}</td>
        <td className="px-3 py-2 font-mono text-xs text-text-muted">{row.target}</td>
        <td className="px-3 py-2">
          <button
            type="button"
            aria-label={open ? 'Zwiń' : 'Rozwiń payload'}
            onClick={() => setOpen((v) => !v)}
            className="text-text-muted hover:text-text"
          >
            {open ? (
              <ChevronDown className="size-4" aria-hidden="true" />
            ) : (
              <ChevronRight className="size-4" aria-hidden="true" />
            )}
          </button>
        </td>
      </tr>
      {open ? (
        <tr className="bg-surface">
          <td colSpan={6} className="px-3 py-2">
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all text-[10px] text-text">
              {tryFormatJson(row.payload_json)}
            </pre>
            <div className="mt-2 grid gap-1 text-[10px] text-text-subtle md:grid-cols-2">
              <span className="font-mono">prev_hash: {row.prev_hash || '—'}</span>
              <span className="font-mono">hash: {row.hash || '—'}</span>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function tryFormatJson(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}
